/**
 * @graphql cc
 */
// TODO - COMMENTS-4934: Delete this file when the 'confluence_frontend_comments_a11y_kb' FG is removed
import { graphql } from 'react-relay';
import gql from 'graphql-tag';
import type { FC } from 'react';
import React, { Fragment, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { styled, css } from '@compiled/react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import type { MutationUpdaterFn } from 'apollo-client';
import type { DataProxy } from 'apollo-cache';

import { token } from '@atlaskit/tokens';
import { IconButton } from '@atlaskit/button/new';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss, Pressable, Text } from '@atlaskit/primitives';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import CommentAddIcon from '@atlaskit/icon/core/comment-add';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';

import {
	VIEW_REACTIONS_EXPERIENCE,
	DELETE_INLINE_COMMENT_EXPERIENCE,
	DELETE_PAGE_COMMENT_EXPERIENCE,
	EDIT_PAGE_COMMENT_EXPERIENCE,
	EDIT_PAGE_COMMENT_LOAD_EXPERIENCE,
	EDIT_PAGE_COMMENT_PUBLISH_EXPERIENCE,
	EDIT_INLINE_COMMENT_EXPERIENCE,
	EDIT_INLINE_COMMENT_LOAD_EXPERIENCE,
	EDIT_INLINE_COMMENT_PUBLISH_EXPERIENCE,
	RESOLVE_INLINE_COMMENT_EXPERIENCE,
	RESOLVE_PAGE_COMMENT_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { startEditorExperiences } from '@confluence/experience-tracker/entry-points/startExperienceForRoute';
import { DisabledCommentAction } from '@confluence/comment/entry-points/DisabledCommentAction';
import { fg } from '@confluence/feature-gating';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import type { ReactionContainerType } from '@confluence/reactions/entry-points/types';
import { ReactionLocation } from '@confluence/reactions/entry-points/types';
import { Reactions } from '@confluence/reactions/entry-points/Reactions';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent';
import { ReactionsContext } from '@confluence/comment-context/entry-points/ReactionsContext';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { getCachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData';
import { usePageContentId } from '@confluence/page-context';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import {
	type ReactionsSummary,
	type CommentInlineResolveProperties,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery';
import {
	useCommentsDataActions,
	CommentType,
} from '@confluence/comments-data/entry-points/useCommentsData';
import {
	isClipboardApiSupported,
	isIEClipboardApiSupported,
	copyToClipboard,
	copyToClipboardIE,
} from '@confluence/comments-util/entry-points/domUtils';
import { AnalyticsSource, ActionName } from '@confluence/comments-util/entry-points/analytics';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useCommentService } from '@confluence/content-types-bridge-comments';
import { preloadDataRetentionPolicy } from '@confluence/data-lifecycle-management/entry-points/queries';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n';
import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop';
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop';

import { InlineCommentMode } from './enum/InlineCommentMode';
import { InlineCommentFramework } from './enum/InlineCommentFramework';
import type { CommentPermissions, InlineCommentsMode, CommentAction } from './inlineCommentsTypes';
import { ActionsContainer } from './styled-components';
import { i18n } from './inlineCommentsi18n';
import { inlineCommentExists } from './inlineCommentsCommonUtils';
import type {
	CommentActionsReactionsCommentsQuery as CommentActionsReactionsCommentsQuery$data,
	CommentActionsReactionsCommentsQueryVariables as CommentActionsReactionsCommentsQuery$variables,
} from './__types__/CommentActionsReactionsCommentsQuery';
import type { CommentActionsReadReactionsSummaryFragment$data } from './__generated__/CommentActionsReadReactionsSummaryFragment.graphql';
import type { CommentActionsWriteReactionsSummaryFragment$data } from './__generated__/CommentActionsWriteReactionsSummaryFragment.graphql';

const CommentActionsReactionsCommentsQuery = gql`
	query CommentActionsReactionsCommentsQuery($contentId: ID!, $containerId: ID!) {
		comments(commentId: $contentId, contentStatus: [DRAFT, CURRENT]) {
			nodes {
				id
				reactionsSummary(pageId: $containerId, contentType: "page", childType: "comment") {
					reactionsCount
					ari
					containerAri
					reactionsSummaryForEmoji {
						emojiId
						count
						reacted
						id
					}
				}
			}
		}
	}
`;
const reactionBelowCommentBodyStyle = xcss({
	marginTop: 'space.0',
});

const itemStyles = css({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	gap: token('space.075'),
});

const reactionInCommentPanelStyle = xcss({
	paddingTop: 'space.050',
	marginLeft: 'space.200',
	paddingLeft: 'space.300',
	paddingBottom: 'space.100',
});

const reactionInCommentPanelStyleV2 = xcss({
	paddingTop: 'space.050',
	paddingBottom: 'space.100',
});

const replyReactionInCommentPanelStyle = xcss({
	marginLeft: 'space.0',
	paddingLeft: 'space.0',
	paddingTop: 'space.150',
	paddingBottom: 'space.0',
});

const standaloneNewDesign = xcss({
	marginLeft: 'space.0',
});

const withLeftBorderStyle = xcss({
	borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
});

const customPaddingStyle = xcss({
	paddingLeft: 'space.0',
});

// As footer does not have isDangling
type CustomProps = Omit<CommentInlineResolveProperties, 'isDangling'> & {
	isDangling?: boolean;
};

type CommentActionsProps = {
	pageId: string;
	pageType: string;
	commentId: string;
	permissions: CommentPermissions;
	isReply: boolean;
	parentCommentId?: string | null;
	restrictEdit?: boolean;
	restrictDelete?: boolean;
	deleteComment?: () => void;
	editComment?: () => void;
	resolveComment?: () => void;
	numReplies?: number;
	isCommentActive?: boolean;
	mode: InlineCommentsMode;
	onClose?: () => void;
	overflowMenuPortal?: HTMLDivElement | null;
	supportedActions?: CommentAction[];
	isCommentsPanel?: boolean;
	isHovered?: boolean;
	annotationId?: string;
	resolveProperties?: CustomProps;
	reopenComment?: () => void;
	replyToComment?: (commentId: string) => void;
	isUnread?: boolean;
	inheritedReactionsData?: ReactionsSummary | null;
	reactionsCacheUpdateFn?: (
		emojiId: string,
		actionType: 'add' | 'delete',
		contentId: string,
		containerId?: string,
		cache?: DataProxy,
	) => MutationUpdaterFn<any> | void;
	commentDateUrl?: string;
	isResolvedByAnotherUser?: boolean;
	commentType: CommentType;
	hideBranchingStyle?: boolean;
	canAddComments?: boolean;
	isDeletedByAnotherUser?: boolean;
	isSelected?: boolean;
	isParentCommentOpen?: boolean;
	isFooterComment?: boolean;
	isStandaloneComment?: boolean;
	isOffline?: boolean;
	isFabricPage?: boolean;
	authorName?: string | null;
	isArchived?: boolean;
};

type DropdownMenuContainerProps = {
	isCommentActive?: boolean;
	mode: InlineCommentsMode;
	showClose: boolean;
	enableDropdownForEditMode: boolean;
	isCommentsPanel: boolean;
	isUnread: boolean;
	canAddComments?: boolean;
};

// WS-2470: We need to override this for correct placement of disabled actions in the overflow menu
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OverflowDisabledDropdownItem = styled(DropdownItem)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	padding: '0 !important',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownMenuContainer = styled.span<DropdownMenuContainerProps>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	`
    	${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles */ ''}
    position: ${(props: DropdownMenuContainerProps) => (props.isCommentsPanel ? 'relative' : 'absolute')};
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
    top: ${(props: DropdownMenuContainerProps) =>
			props.isCommentsPanel
				? '0px'
				: props.mode === 'view'
					? '-20px'
					: props.enableDropdownForEditMode
						? token('space.negative.400')
						: props.showClose
							? token('space.negative.050')
							: token('space.negative.025')};
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
    right: ${(props: DropdownMenuContainerProps) =>
			props.isCommentsPanel
				? 'unset'
				: props.mode !== 'view'
					? props.enableDropdownForEditMode
						? token('space.300')
						: props.showClose
							? token('space.300')
							: token('space.050')
					: props.isCommentsPanel
						? !props.isUnread
							? token('space.0')
							: token('space.300')
						: token('space.500')};
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
    visibility: ${(props: DropdownMenuContainerProps) => (!props.isCommentActive && props.mode === 'view-all' ? 'hidden' : 'visible')};

    button, button:hover {
      width: 24px;
      height: 24px;
      border-radius: 4px;
    };
    & span[role="img"], > svg {
      height: 20px;
      width: 20px;
    }
  `,
);

const clickableItemStyle = xcss({
	cursor: 'pointer',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const ReplyContainer = styled.div({
	alignContent: 'center',
	cursor: 'pointer',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const ReactionsContainer = styled.div<{
	hasReactions: boolean;
	isCommentsPanel: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	display: (props) => (props.hasReactions ? 'block' : 'inline-flex'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	flexBasis: (props) => (props.hasReactions ? '100%' : 'auto'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	marginTop: (props) =>
		!props.isCommentsPanel && !props.hasReactions ? token('space.050') : token('space.0'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	marginLeft: (props) =>
		!props.isCommentsPanel && !props.hasReactions ? token('space.050') : token('space.0'),
});

type ActionItemProps = {
	mode?: InlineCommentsMode;
	isReply?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const ActionItem = styled.li<ActionItemProps>(`
  display: inline-flex;
  flex-flow: row nowrap;
  color: ${token('color.text.subtle')};
  min-height: 24px;
  align-items: center;

  a {
    align-items: flex-end;
    color: ${token('color.text.brand')};
    flex: 2;
    text-align: right;
    font: ${token('font.body.small')};
    padding-top: ${token('space.050')};
    cursor: pointer;
  }

  & button {
    color: ${token('color.text.subtle')};
  }

  & button:focus {
    outline: 2px solid ${token('color.border.focused')};
  }

  &:not(:last-of-type):after {
    content: "•";
    color: ${token('color.text.subtle')};
    padding: 0 ${token('space.050')} 0 ${token('space.050')};
  }

  &:first-of-type:after {
    display: none;
  }
`);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const ActionButton = styled.button(`
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font-size: inherit;
  cursor: pointer;
  outline: inherit;

  &:focus {
    outline: 2px solid ${token('color.border.focused')};
    outline-offset: 2px;
  }

  :hover span {
    text-decoration: underline;
  }
`);

const pressableStyles = xcss({
	':hover': {
		textDecoration: 'underline',
	},
});

const rightAlignedActionItemsStyle = xcss({
	position: 'absolute',
	right: 'space.0',
	display: 'flex',
	gap: 'space.075',
	justifyContent: 'space-between',
	alignItems: 'center',
	paddingLeft: 'space.150',
});

const rightAlignedActionItemsCommentsPanelStyle = xcss({
	top: 'space.0',
});

const rightAlignedItemsStandaloneStyle = xcss({
	top: 'space.150',
	right: 'space.200',
});

const rightAlignedActionItemsFooterCommentStyle = xcss({
	top: 'space.100',
});

const hoveredBackgroundInSidePanel = xcss({
	background: token('elevation.surface.raised'),
});

const hoveredBackgroundCommentSurface = xcss({
	background: token('elevation.surface'),
});

// TODO: remove oldHoveredBackground when cleaning up `confluence_frontend_content_wrapper`
const oldHoveredBackground = xcss({
	background: token('color.background.input'),
});

const selectedBackground = xcss({
	background: token('color.background.input.hovered'),
});

const resolvedTextStyle = css({
	color: token('color.text.subtle'),
	font: token('font.heading.xxsmall'),
});

const softBorderBase = css({
	overflow: 'hidden',
	position: 'relative',
	maxHeight: 'inherit',
	borderRadius: token('radius.small'),
});

const softBorderPaddingOverride = xcss({
	paddingLeft: 'space.050',
});

export const CommentActions: FC<CommentActionsProps> = ({
	pageId,
	pageType,
	commentId,
	permissions,
	isReply,
	parentCommentId,
	restrictEdit,
	restrictDelete,
	deleteComment,
	editComment,
	resolveComment,
	isCommentActive,
	mode,
	onClose,
	overflowMenuPortal,
	supportedActions = ['edit', 'delete', 'resolve'],
	isCommentsPanel = false,
	isHovered,
	annotationId,
	resolveProperties,
	reopenComment,
	replyToComment,
	isUnread = false,
	inheritedReactionsData,
	reactionsCacheUpdateFn,
	commentDateUrl,
	isResolvedByAnotherUser,
	commentType,
	hideBranchingStyle = false,
	canAddComments = true,
	isDeletedByAnotherUser = false,
	isSelected = false,
	isParentCommentOpen,
	isFooterComment = false,
	isStandaloneComment = false,
	isOffline = false,
	numReplies,
	isFabricPage = true,
	authorName,
	isArchived,
}) => {
	const deletePressTracing = usePressTracing('delete-comment-action');
	const editPressTracing = usePressTracing('start-edit-comment-action');
	const replyPressTracing = usePressTracing('start-reply-comment-action');
	const resolvePressTracing = usePressTracing('resolve-comment-action');

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [showCommentActionItems, setShowCommentActionItems] = useState(false);
	const [isEditable, setIsEditable] = useState(false);
	const [isRemovable, setIsRemovable] = useState(false);
	const [isResolvable, setIsResolvable] = useState(false);
	const [canBeReopened, setCanBeReopened] = useState(false);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { isReactionsEnabled } = useContext(ReactionsContext);
	const [reactionsCount, setReactionsCount] = useState(inheritedReactionsData?.reactionsCount || 0);
	const [reactionParticleEffectByEmoji, setReactionParticleEffectByEmoji] = useState<
		Record<string, boolean>
	>({});

	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();
	const { showModal } = useDialogs();
	const { updateReactionsSummary } = useCommentsDataActions();

	const readReactionsSummaryFragment =
		useReadFragmentInterop<CommentActionsReadReactionsSummaryFragment$data>({
			fragmentSpec: graphql`
				fragment CommentActionsReadReactionsSummaryFragment on ReactionsSummaryResponse {
					ari
					containerAri
					reactionsCount
					reactionsSummaryForEmoji {
						id
						emojiId
						count
						reacted
					}
				}
			`,
		});

	const writeReactionsSummaryFragment = useWriteFragmentInterop<
		Omit<CommentActionsWriteReactionsSummaryFragment$data, ' $fragmentType'>
	>({
		fragmentSpec: graphql`
			fragment CommentActionsWriteReactionsSummaryFragment on ReactionsSummaryResponse {
				ari
				containerAri
				reactionsCount
				reactionsSummaryForEmoji {
					id
					emojiId
					count
					reacted
				}
			}
		`,
	});

	const { edition, cloudId } = useSessionData();

	const { commentService } = useCommentService();

	const isLivePage = useIsCurrentPageLive();

	const [contentId] = usePageContentId();
	const { subType } = getCachedPageData(contentId);

	const { formatMessage } = useIntl();

	const handleGqlError = (error: Error) => {
		experienceTracker.stopOnError({
			name: VIEW_REACTIONS_EXPERIENCE,
			error,
		});
	};
	const { data: reactionsData, loading: reactionsLoading } = useQuery<
		CommentActionsReactionsCommentsQuery$data,
		CommentActionsReactionsCommentsQuery$variables
	>(CommentActionsReactionsCommentsQuery, {
		variables: { contentId: commentId, containerId: pageId },
		onError: handleGqlError,
		fetchPolicy: 'cache-and-network',
		// If we have the reactions from a higher component, skip the query
		skip: Boolean(inheritedReactionsData),
	});

	useEffect(() => {
		// If we have the reactions from a higher component, use that count
		const count =
			(inheritedReactionsData === undefined
				? reactionsData?.comments?.nodes?.[0]?.reactionsSummary?.reactionsCount
				: inheritedReactionsData?.reactionsCount) || 0;

		setReactionsCount(count);
	}, [reactionsData, inheritedReactionsData]);

	useEffect(() => {
		const disabledByOfflineMode =
			isOffline && expVal('platform_editor_offline_editing_web', 'isEnabled', false);
		// Actions are decided by the mode we're displaying
		// and the permissions available for the user
		const canEdit = Boolean(
			permissions?.isEditable &&
				editComment &&
				supportedActions.includes('edit') &&
				!disabledByOfflineMode,
		);
		const canDelete = Boolean(
			permissions?.isRemovable &&
				deleteComment &&
				supportedActions.includes('delete') &&
				!disabledByOfflineMode,
		);
		const canResolve = Boolean(
			permissions?.isResolvable &&
				resolveComment &&
				supportedActions.includes('resolve') &&
				!disabledByOfflineMode,
		);

		// if user doesn't have resolve comment permission then they shouldn't have reopen permission too
		const hasPermissionToReopen =
			reopenComment && supportedActions.includes('reopen') && permissions?.isResolvable;
		const canReopen = Boolean(hasPermissionToReopen && !isReply) && !disabledByOfflineMode;
		// handle reopening dangling comments
		if (resolveProperties?.resolved && resolveProperties?.isDangling) {
			if (!fg('confluence_frontend_content_wrapper')) {
				setCanBeReopened(false);
			} else {
				setCanBeReopened(canReopen);
			}

			// handle reopening non-dangling comments
		} else {
			setCanBeReopened(
				Boolean(
					canReopen &&
						(resolveProperties?.resolved || isResolvedByAnotherUser) &&
						((annotationId && inlineCommentExists(annotationId)) ||
							commentType === CommentType.GENERAL),
				),
			);
		}

		if (mode === 'view' || mode === 'edit') {
			if (!isReply) {
				setIsResolvable(canResolve); // Parent comments are resolvable if allowed
			} else {
				setIsResolvable(false); // Replies cannot be resolved
			}
			// Comments panel is only rendered for fabric pages not on the legacy
			if (isCommentsPanel || (isFabricPage && fg('confluence_frontend_content_wrapper'))) {
				const hasPermissions = canEdit || canDelete || canResolve;
				const shouldShowDropdownMenu = fg('confluence_frontend_content_wrapper')
					? true
					: !!isHovered && !fg('confluence_frontend_content_wrapper') && hasPermissions;

				// Set the state for using the dropdown menu
				setShowCommentActionItems(shouldShowDropdownMenu);
			} else if (!isReply && (canEdit || canDelete || canResolve)) {
				setShowCommentActionItems(true); // Parent comments or replies outside panel can show menu
			}

			// Users can delete the replies they created
			setIsRemovable(canDelete);
			setIsEditable(canEdit);
		} else {
			setIsRemovable(canDelete);
			setIsEditable(canEdit);
			setIsResolvable(canResolve && !isReply);
		}
	}, [
		mode,
		permissions,
		isHovered,
		isReply,
		isCommentsPanel,
		supportedActions,
		deleteComment,
		editComment,
		resolveComment,
		reopenComment,
		resolveProperties?.isDangling,
		annotationId,
		resolveProperties?.resolved,
		isResolvedByAnotherUser,
		commentType,
		isOffline,
		isFabricPage,
	]);

	useEffect(() => {
		// If the user is on the comments panel and leaves this comment and the dropdown menu is open,
		// close the dropdown menu so they don't see it when they return
		if (
			isCommentsPanel &&
			isDropdownOpen &&
			!fg('confluence_frontend_content_wrapper') &&
			!isHovered
		) {
			setIsDropdownOpen(false);
		}
	}, [isCommentsPanel, isDropdownOpen, isHovered, setIsDropdownOpen]);

	const getInlineCommentModeConstant = () => {
		switch (mode) {
			case 'view':
				return InlineCommentMode.VIEW;
			case 'edit':
				if (isLivePage) {
					return InlineCommentMode.LIVE;
				}
				return InlineCommentMode.EDIT;
		}
	};

	const handleDelete = () => {
		if (commentType === CommentType.INLINE) {
			experienceTracker.start({
				name: DELETE_INLINE_COMMENT_EXPERIENCE,
				attributes: {
					mode: getInlineCommentModeConstant(),
					framework: InlineCommentFramework.ANNOTATION_PROVIDER,
					isReply,
					source: isCommentsPanel ? AnalyticsSource.COMMENTS_PANEL : AnalyticsSource.STANDALONE,
					annotationId,
				},
			});
		} else {
			experienceTracker.start({
				name: DELETE_PAGE_COMMENT_EXPERIENCE,
				attributes: {
					isReply,
				},
			});
		}

		showModal(CommentWarningDialog, {
			onConfirm: () => {
				deleteCommentWithPressTracing();
			},
			isDelete: true,
			onCancel: () => {
				// abort delete experience if dialog is closed
				experienceTracker.abort({
					name:
						commentType === CommentType.INLINE
							? DELETE_INLINE_COMMENT_EXPERIENCE
							: DELETE_PAGE_COMMENT_EXPERIENCE,
					reason: 'delete dialog closed',
				});
			},
		});
	};

	const deleteCommentWithPressTracing = () => {
		if (!!deleteComment) {
			deletePressTracing();
			deleteComment();
		}
	};

	const handleEditComment = () => {
		if (commentType === CommentType.INLINE) {
			startEditorExperiences(
				/* compoundExperience */ {
					name: EDIT_INLINE_COMMENT_EXPERIENCE,
					attributes: {
						mode: getInlineCommentModeConstant(),
						framework: InlineCommentFramework.ANNOTATION_PROVIDER,
						isReply,
						type: isReply ? 'reply' : 'topLevel',
						annotationId,
					},
				},
				/* editorLoadExperience */ {
					name: EDIT_INLINE_COMMENT_LOAD_EXPERIENCE,
				},
				/* editorPublishExperience */ {
					name: EDIT_INLINE_COMMENT_PUBLISH_EXPERIENCE,
				},
			);
		} else {
			startEditorExperiences(
				/* compoundExperience */ {
					name: EDIT_PAGE_COMMENT_EXPERIENCE,
					attributes: {
						editor: 'FABRIC',
						isSingleExperience: true,
						edition,
						type: isReply ? 'reply' : 'topLevel',
						source: isFooterComment ? AnalyticsSource.PAGE_FOOTER : AnalyticsSource.COMMENTS_PANEL,
					},
				},
				/* editorLoadExperience */ {
					name: EDIT_PAGE_COMMENT_LOAD_EXPERIENCE,
				},
				/* editorPublishExperience */ {
					name: EDIT_PAGE_COMMENT_PUBLISH_EXPERIENCE,
				},
			);
		}

		editComment && editComment();
	};

	const handleEditCommentWithPressTracing = () => {
		editPressTracing();
		handleEditComment();
	};

	const handleReply = () => {
		const replyToCommentId = isFooterComment ? commentId : parentCommentId || commentId;

		if (hasContentChanged) {
			showModal(CommentWarningDialog, {
				onConfirm: () => {
					resetContentChanged();
					replyToCommentWithPressTracing(replyToCommentId);
				},
			});
		} else {
			replyToCommentWithPressTracing(replyToCommentId);
		}
	};

	const replyToCommentWithPressTracing = (replyToCommentId: string) => {
		if (!!replyToComment) {
			replyPressTracing();
			replyToComment(replyToCommentId);
		}
	};

	const handleCopyLink = async () => {
		const linkToComment = `${window.location.origin}/wiki${commentDateUrl}`;

		if (isClipboardApiSupported()) {
			await copyToClipboard(linkToComment);
		} else if (isIEClipboardApiSupported()) {
			await copyToClipboardIE(linkToComment);
		}
	};

	const handleResolveComment = () => {
		const doResolve = () => {
			if (commentType === CommentType.INLINE) {
				experienceTracker.start({
					name: RESOLVE_INLINE_COMMENT_EXPERIENCE,
					id: commentId,
					attributes: {
						mode: getInlineCommentModeConstant(),
						framework: InlineCommentFramework.ANNOTATION_PROVIDER,
						annotationId,
					},
				});
			} else {
				experienceTracker.start({
					name: RESOLVE_PAGE_COMMENT_EXPERIENCE,
					id: commentId,
					attributes: {
						annotationId,
					},
				});
			}

			resolveCommentWithPressTracing();
		};

		if (hasContentChanged) {
			showModal(CommentWarningDialog, {
				onConfirm: () => {
					resetContentChanged();
					doResolve();
				},
			});
		} else {
			doResolve();
		}
	};

	const resolveCommentWithPressTracing = () => {
		if (!!resolveComment) {
			resolvePressTracing();
			resolveComment();
		}
	};

	const handleReopenComment = () => {
		reopenComment && reopenComment();
	};

	const handleParticleEffectChange = useCallback((emojiId: string, show: boolean) => {
		setReactionParticleEffectByEmoji((prev) => ({
			...prev,
			[emojiId]: show,
		}));
	}, []);

	const renderEditAction = (isRestricted?: boolean, isDropdownMenu?: boolean) => {
		if (isDropdownMenu) {
			return isRestricted ? (
				<OverflowDisabledDropdownItem testId="disabled-edit-comment-dropdown-action">
					<DisabledCommentAction action="edit" isReply={isReply} isInOverflowMenu />
				</OverflowDisabledDropdownItem>
			) : (
				<DropdownItem
					testId="edit-comment-dropdown-action"
					data-cy="edit-comment-action"
					onClick={handleEditComment}
					interactionName="start-edit-comment-action"
				>
					<FormattedMessage {...i18n.edit} />
				</DropdownItem>
			);
		}

		return isRestricted ? (
			<DisabledCommentAction action="edit" isReply={isReply} isInOverflowMenu />
		) : (
			<ActionButton
				data-testid="edit-comment-action"
				data-cy="edit-comment-action"
				onClick={handleEditCommentWithPressTracing}
			>
				<FormattedMessage {...i18n.edit} />
			</ActionButton>
		);
	};

	const renderDeleteAction = (isRestricted?: boolean, isDropdownMenu?: boolean) => {
		if (isDropdownMenu) {
			return isRestricted ? (
				<OverflowDisabledDropdownItem testId="disabled-delete-comment-dropdown-action">
					<DisabledCommentAction action="delete" isReply={isReply} isInOverflowMenu />
				</OverflowDisabledDropdownItem>
			) : (
				<DropdownItem testId="delete-comment-dropdown-action" onClick={handleDelete}>
					<FormattedMessage {...i18n.delete} />
				</DropdownItem>
			);
		}

		return isRestricted ? (
			<DisabledCommentAction action="delete" isReply={isReply} />
		) : (
			<ActionButton
				data-testid="delete-comment-action"
				onClick={handleDelete}
				onMouseEnter={() => preloadDataRetentionPolicy({ cloudId })}
			>
				<FormattedMessage {...i18n.delete} />
			</ActionButton>
		);
	};

	const renderReopenComment = () => {
		return (
			canBeReopened && (
				<div css={itemStyles}>
					{/* this means we're in resolved view in the comments panel */}
					{isResolvedByAnotherUser && !fg('confluence_frontend_content_wrapper') && (
						<div css={resolvedTextStyle}>{formatMessage(i18n.resolved)}</div>
					)}
					<IconButton
						icon={(iconProps) => (
							<StatusSuccessIcon
								{...iconProps}
								spacing="spacious"
								color={token('color.icon.success')}
								label={formatMessage(i18n.reopen)}
							/>
						)}
						label={formatMessage(i18n.reopen)}
						isTooltipDisabled={false}
						onClick={handleReopenComment}
						appearance="subtle"
						testId="reopen-comment-action"
						analyticsContext={{
							attributes: {
								name: ActionName.REOPEN_COMMENT,
								source: isCommentsPanel
									? AnalyticsSource.COMMENTS_PANEL
									: AnalyticsSource.STANDALONE,
								isDanglingComment: resolveProperties?.isDangling, // all dangling comments are inline comments
							},
						}}
					/>
				</div>
			)
		);
	};

	const renderReplyAction = () => {
		if (!isCommentsPanel && !fg('confluence_frontend_comments_panel_v2')) return;
		return (
			<ReplyContainer>
				<ActionButton
					data-testid="reply-comment-action"
					data-cy="reply-comment-action"
					onClick={handleReply}
				>
					<FormattedMessage {...i18n.replyPlaceholder} />
				</ActionButton>
			</ReplyContainer>
		);
	};

	const renderCopyButton = () => {
		return (
			<DropdownItem testId="copy-link-dropdown-action" onClick={handleCopyLink}>
				<FormattedMessage {...i18n.copyLink} />
			</DropdownItem>
		);
	};

	const renderResolveOption = () => {
		if (isCommentsPanel && isReply && fg('confluence-frontend-comments-panel')) {
			return;
		}
		return (
			<DropdownItem testId="resolve-comment-dropdown-action" onClick={handleResolveComment}>
				<FormattedMessage {...i18n.resolve} />
			</DropdownItem>
		);
	};

	const renderDropdownMenu = (editRestricted?: boolean, deleteRestricted?: boolean) => {
		// hide the dropdown if not the comments panel and does not have permissions to edit/delete/resolve
		const hasNoCommmentPermissions = !isEditable && !isRemovable && (isReply || !isResolvable);

		// For old UI, use the existing logic
		const shouldHideDropdownForStandalone = !isCommentsPanel && hasNoCommmentPermissions;
		const shouldHideDropdownForCommentsPanel =
			isCommentsPanel && hasNoCommmentPermissions && !fg('confluence_frontend_comments_panel_v2');

		if (shouldHideDropdownForStandalone || shouldHideDropdownForCommentsPanel) {
			return <></>;
		}

		const dropdownMenu = (
			<DropdownMenuContainer
				data-cy="comment-overflow-actions-menu"
				data-testid="comment-overflow-actions-menu"
				isCommentActive={isCommentActive}
				mode={mode}
				showClose={Boolean(onClose)}
				enableDropdownForEditMode={fg('confluence-frontend-comments-panel') || mode === 'edit'}
				isCommentsPanel={
					(isCommentsPanel || fg('confluence_frontend_content_wrapper')) && isFabricPage
				}
				isUnread={isUnread}
			>
				{/* TODO Add `shouldRenderToParent` prop to resolve accessibility issues. See https://a11y.atlassian.net/browse/JCATS-788 */}
				<DropdownMenu
					testId="comment-overflow-menu"
					isOpen={isDropdownOpen}
					placement="bottom-end"
					onOpenChange={({ isOpen, event }) => {
						event?.stopPropagation();
						setIsDropdownOpen(isOpen);
						if (isRemovable) void preloadDataRetentionPolicy({ cloudId });
					}}
					trigger={({ triggerRef, ...props }) => (
						<IconButton
							{...props}
							testId="comment-overflow-menu-trigger"
							ref={triggerRef}
							label={formatMessage(i18n.commentActionsMenuLabel)}
							appearance="subtle"
							icon={ShowMoreHorizontalIcon}
							isTooltipDisabled={false}
							isSelected={isDropdownOpen}
							analyticsContext={{
								attributes: {
									name: 'moreActionsButton',
									source: isCommentsPanel
										? AnalyticsSource.COMMENTS_PANEL
										: AnalyticsSource.STANDALONE,
									commentType,
								},
							}}
						/>
					)}
				>
					<DropdownItemGroup>
						{isFabricPage &&
							(isCommentsPanel || fg('confluence_frontend_content_wrapper')) &&
							fg('confluence_frontend_comments_panel_v2') &&
							renderCopyButton()}
						{((!isCommentsPanel && !fg('confluence_frontend_content_wrapper')) ||
							(!isFabricPage && !isFooterComment)) &&
							isResolvable &&
							renderResolveOption()}
						{isEditable && renderEditAction(editRestricted, showCommentActionItems)}
						{isRemovable && renderDeleteAction(deleteRestricted, showCommentActionItems)}
					</DropdownItemGroup>
				</DropdownMenu>
			</DropdownMenuContainer>
		);

		return overflowMenuPortal && !fg('confluence_frontend_content_wrapper')
			? createPortal(dropdownMenu, overflowMenuPortal)
			: dropdownMenu;
	};

	const reactionsUpdateFn = useMemo(() => {
		if (reactionsCacheUpdateFn) {
			return reactionsCacheUpdateFn;
		}

		return inheritedReactionsData !== undefined && isCommentsPanel
			? (
					emojiId: string,
					actionType: 'add' | 'delete',
					objectId: string,
					containerId?: string,
					cache?: DataProxy,
				) => {
					// Update particle effect state when adding/removing reactions
					setReactionParticleEffectByEmoji((prev) => ({
						...prev,
						[emojiId]: actionType === 'add',
					}));

					if (annotationId && emojiId && actionType) {
						// Update the reaction in the content types bridge
						void commentService?.onUpdateReaction(annotationId, emojiId, actionType);
					}

					return updateReactionsSummary(
						emojiId,
						actionType,
						objectId,
						commentType,
						readReactionsSummaryFragment,
						writeReactionsSummaryFragment,
						containerId,
						annotationId,
						cache,
					);
				}
			: undefined;
	}, [
		inheritedReactionsData,
		reactionsCacheUpdateFn,
		isCommentsPanel,
		annotationId,
		commentType,
		updateReactionsSummary,
		commentService,
		readReactionsSummaryFragment,
		writeReactionsSummaryFragment,
	]);

	const renderReplyButton = () => {
		return (
			<IconButton
				testId="comment-reply-button"
				label={
					fg('confluence_frontend_comments_a11y_kb')
						? formatMessage(i18n.replyPlaceholderWithName, {
								name: authorName || formatMessage(commentsi18n.anonymous),
							})
						: formatMessage(i18n.replyPlaceholder)
				}
				appearance="subtle"
				spacing="compact"
				icon={CommentAddIcon}
				onClick={handleReply}
				isTooltipDisabled={false}
			/>
		);
	};

	const renderResolveActionButton = () => {
		return (
			<IconButton
				testId="comment-resolve-action-item"
				label={
					fg('confluence_frontend_comments_a11y_kb')
						? formatMessage(i18n.resolveThread)
						: formatMessage(i18n.resolve)
				}
				appearance="subtle"
				spacing="compact"
				icon={CheckMarkIcon}
				onClick={handleResolveComment}
				isTooltipDisabled={false}
				css={{ padding: 0 }}
				analyticsContext={{
					attributes: {
						name: 'resolveActionsButton',
						source: isCommentsPanel ? AnalyticsSource.COMMENTS_PANEL : AnalyticsSource.STANDALONE,
						commentType,
					},
				}}
			/>
		);
	};

	const renderReactions = ({
		onlyRenderPicker = false,
		readOnly = false,
	}: {
		onlyRenderPicker?: boolean;
		readOnly?: boolean;
	} = {}) => {
		return (
			<ReactionsContainer
				hasReactions={reactionsCount > 0}
				isCommentsPanel={
					(isCommentsPanel || fg('confluence_frontend_content_wrapper')) && isFabricPage
				}
				onClick={(e) =>
					// Stop the click event from propagating to the outer comments container to prevent triggering the scroll-to-comment function
					e.stopPropagation()
				}
				{...(fg('confluence_frontend_comments_a11y_kb')
					? {
							'aria-label': formatMessage(i18n.reactionAriaLabelWithName, {
								name: authorName || formatMessage(commentsi18n.anonymous),
							}),
						}
					: {})}
			>
				<Reactions
					readOnly={readOnly}
					contentId={commentId}
					containerId={pageId}
					containerType={pageType as ReactionContainerType}
					location={
						mode === 'edit'
							? ReactionLocation.EDITOR_INLINE_COMMENT
							: ReactionLocation.INLINE_COMMENT
					}
					hidePlaceholder={
						reactionsCount === 0 ||
						inheritedReactionsData !== undefined ||
						inheritedReactionsData !== null
					}
					reactionsData={
						inheritedReactionsData === undefined || inheritedReactionsData === null
							? reactionsData?.comments?.nodes?.[0]?.reactionsSummary
							: inheritedReactionsData
					}
					updateCache={reactionsUpdateFn}
					onlyRenderPicker={onlyRenderPicker}
					isSubtle={isCommentsPanel || fg('confluence_frontend_content_wrapper')}
					showAddReactionText={false}
					customAnalyticsAttributes={{
						source: isCommentsPanel ? AnalyticsSource.COMMENTS_PANEL : AnalyticsSource.STANDALONE,
						isReply,
						mode,
					}}
					contentSubType={subType}
					isListItem={false}
					key={`reactions-${commentId}-${onlyRenderPicker}`}
					particleEffectByEmoji={reactionParticleEffectByEmoji}
					onParticleEffectChange={handleParticleEffectChange}
				/>
			</ReactionsContainer>
		);
	};

	const renderV1ReopenComment = () => {
		return (
			<Pressable
				testId="old-reopen-comment-action"
				onClick={handleReopenComment}
				padding="space.0"
				backgroundColor="color.background.neutral.subtle"
				xcss={pressableStyles}
			>
				<Text color="color.text.subtlest" size="small">
					<FormattedMessage {...i18n.reopen} />
				</Text>
			</Pressable>
		);
	};

	// An abstraction made by tracing through the custom xcss returned in renderActionItemsOnHover to return the correct
	// background color. When migrating to compiled, the conditional styling needs to be simplified to reduce duplication
	const getActionItemsBackgroundColor = () => {
		if (isCommentsPanel) {
			if (isSelected) {
				return token('color.background.input.hovered');
			} else if (isHovered) {
				return token('elevation.surface.raised');
			}
		}
		return token('elevation.surface');
	};

	const renderActionItemsOnHover = () => (
		<Box
			data-testId="comment-action-items"
			xcss={[
				rightAlignedActionItemsStyle,
				clickableItemStyle,
				isStandaloneComment && hoveredBackgroundCommentSurface, // for standalone it will always be
				isHovered && !fg('confluence_frontend_content_wrapper') && oldHoveredBackground,
				isHovered &&
					fg('confluence_frontend_content_wrapper') &&
					(isFooterComment || isStandaloneComment || !isCommentsPanel
						? hoveredBackgroundCommentSurface
						: hoveredBackgroundInSidePanel),
				isSelected && !isStandaloneComment && selectedBackground,
				isFooterComment && rightAlignedActionItemsFooterCommentStyle,
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				(isCommentsPanel || fg('confluence_frontend_content_wrapper')) &&
					isFabricPage &&
					!isFooterComment &&
					rightAlignedActionItemsCommentsPanelStyle,
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				!isCommentsPanel &&
					!isFooterComment &&
					fg('confluence_frontend_content_wrapper') &&
					isFabricPage &&
					rightAlignedItemsStandaloneStyle,
				isCommentsPanel &&
					isReply &&
					fg('confluence_frontend_content_wrapper') &&
					rightAlignedActionItemsFooterCommentStyle,
				expValEquals('confluence_cohiti_comment_hover_actions', 'isEnabled', true) &&
					softBorderPaddingOverride,
			]}
		>
			<div
				css={[
					itemStyles,
					expValEquals('confluence_cohiti_comment_hover_actions', 'isEnabled', true) &&
						softBorderBase,
				]}
				style={{
					boxShadow: expValEquals('confluence_cohiti_comment_hover_actions', 'isEnabled', true)
						? `-10px 0 10px 0px ${getActionItemsBackgroundColor()}`
						: '',
				}}
				data-testId="comment-hover-actions-container"
			>
				{!resolveProperties?.resolved &&
					!isResolvedByAnotherUser &&
					(!isReply ||
						isFooterComment ||
						(isCommentsPanel && fg('confluence_frontend_content_wrapper'))) && // Footer replies can be replied to
					!isArchived &&
					canAddComments &&
					renderReplyButton()}
				{isReactionsEnabled && !reactionsLoading && (
					<div>
						{renderReactions({
							onlyRenderPicker: true,
							readOnly:
								isOffline && expVal('platform_editor_offline_editing_web', 'isEnabled', false),
						})}
					</div>
				)}

				{!isReply && !resolveProperties?.resolved && !isResolvedByAnotherUser && isResolvable && (
					<div>{renderResolveActionButton()}</div>
				)}
				<div>{renderDropdownMenu(restrictEdit, restrictDelete)}</div>
			</div>
		</Box>
	);

	const renderStaticActionsItemsForCommentsPanel = () => {
		const isResolved = resolveProperties?.resolved || isResolvedByAnotherUser;
		return (
			<>
				{isResolved && (
					<Box
						xcss={
							fg('confluence_frontend_comments_panel_v2') && [
								rightAlignedActionItemsStyle,
								(isCommentsPanel || fg('confluence_frontend_content_wrapper')) &&
									isFabricPage &&
									!isFooterComment &&
									rightAlignedActionItemsCommentsPanelStyle,
								isFooterComment && rightAlignedActionItemsFooterCommentStyle,
							]
						}
						testId="static-actions-items"
					>
						{fg('confluence_frontend_comments_panel_v2')
							? renderReopenComment()
							: renderV1ReopenComment()}
					</Box>
				)}
			</>
		);
	};

	const renderCommentsPanelActionItems = () => {
		const shouldRenderReplyAction =
			!resolveProperties?.resolved &&
			!isResolvedByAnotherUser &&
			!fg('confluence_frontend_comments_panel_v2');

		const isReadOnlyForResolved = resolveProperties?.resolved || (isReply && !isParentCommentOpen);
		// Using a function here to make sure experiment exposure is only captured when this isn't already read only by the resolved state
		const isDisabledByOfflineMode = () =>
			isOffline && expVal('platform_editor_offline_editing_web', 'isEnabled', false);
		const commonElements = (
			<>
				{shouldRenderReplyAction && renderReplyAction()}
				{isReactionsEnabled && !reactionsLoading && reactionsCount > 0 && (
					<Box
						xcss={[
							reactionBelowCommentBodyStyle,
							(isCommentsPanel || fg('confluence_frontend_content_wrapper')) &&
								isFabricPage &&
								reactionInCommentPanelStyle,
							(isCommentsPanel || fg('confluence_frontend_content_wrapper')) &&
								isFabricPage &&
								fg('confluence_frontend_comments_panel_v2') &&
								reactionInCommentPanelStyleV2,
							(isCommentsPanel || fg('confluence_frontend_content_wrapper')) &&
								isFabricPage &&
								isReply &&
								fg('confluence_frontend_comments_panel_v2') &&
								replyReactionInCommentPanelStyle,
							(isCommentsPanel || fg('confluence_frontend_content_wrapper')) &&
								isFabricPage &&
								!isReply &&
								((!hideBranchingStyle && canAddComments) ||
									(isReadOnlyForResolved && numReplies !== 0)) &&
								fg('confluence_frontend_comments_panel_v2') &&
								withLeftBorderStyle,
							// eslint-disable-next-line confluence-feature-gating/no-preconditioning
							!isCommentsPanel &&
								fg('confluence_frontend_content_wrapper') &&
								isFabricPage &&
								standaloneNewDesign,
							// eslint-disable-next-line confluence-feature-gating/no-preconditioning
							!isCommentsPanel &&
								!isFooterComment &&
								hideBranchingStyle &&
								fg('confluence_frontend_content_wrapper') &&
								isFabricPage &&
								customPaddingStyle,
						]}
					>
						{renderReactions({
							readOnly: isReadOnlyForResolved || isDisabledByOfflineMode(),
						})}
					</Box>
				)}
			</>
		);

		// no hover state for resolved comments
		const shouldRenderHoverState = () => {
			if (isReply && !isParentCommentOpen) {
				return false;
			} else {
				return (
					(isHovered || (isDropdownOpen && fg('confluence_frontend_content_wrapper'))) &&
					!resolveProperties?.resolved &&
					!isDeletedByAnotherUser
				);
			}
		};
		const stateSpecificElements = shouldRenderHoverState()
			? renderActionItemsOnHover()
			: renderStaticActionsItemsForCommentsPanel();

		return (
			<>
				{stateSpecificElements}
				{commonElements}
			</>
		);
	};

	const renderStandaloneCommentActions = () => {
		return (
			<ActionsContainer>
				<ActionItem aria-hidden="true" />
				{!isReply && isResolvable && (
					<ActionItem mode={mode} isReply={isReply}>
						<ActionButton
							data-testid="resolve-comment-action"
							data-cy="resolve-comment-action"
							onClick={handleResolveComment}
						>
							<FormattedMessage {...i18n.resolve} />
						</ActionButton>
					</ActionItem>
				)}
				{!showCommentActionItems && isEditable && (
					<ActionItem mode={mode} isReply={isReply}>
						{renderEditAction(restrictEdit, showCommentActionItems)}
					</ActionItem>
				)}
				{!showCommentActionItems && isRemovable && (
					<ActionItem mode={mode} isReply={isReply}>
						{renderDeleteAction(restrictDelete, showCommentActionItems)}
					</ActionItem>
				)}
				{isReactionsEnabled &&
					!reactionsLoading &&
					!resolveProperties?.resolved &&
					renderReactions({
						readOnly:
							isOffline && expVal('platform_editor_offline_editing_web', 'isEnabled', false),
					})}
				{!resolveProperties?.resolved ||
					// eslint-disable-next-line confluence-feature-gating/no-preconditioning
					(isCommentsPanel && fg('confluence-frontend-comments-panel') && renderReplyAction())}
			</ActionsContainer>
		);
	};

	const renderStandaloneCommentActionItems = () => {
		return (
			<>
				{!isReply && renderDropdownMenu(restrictEdit, restrictDelete)}
				{renderStandaloneCommentActions()}
			</>
		);
	};

	return (
		<Fragment>
			{(isCommentsPanel || fg('confluence_frontend_content_wrapper')) && isFabricPage
				? renderCommentsPanelActionItems()
				: renderStandaloneCommentActionItems()}
		</Fragment>
	);
};
